import { useMemo, useCallback } from "react";
import { Schema, FieldType } from "../../../toolympus/hooks/useSchema";
import { useLoadedData } from "../../../toolympus/hooks/useLoadedData";
import { apiFetch } from "../../../toolympus/api/core";
import { CopTreeNode } from "./useCopTreeNodes";

export interface CopTreeNodeLink {
  _id: number;
  source_node_id: number;
  target_node_id: number;
  relation_type: string;
  description?: string | null;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
}

export const CopTreeNodeLinkSchema: Schema = {
  _id: { label: "ID", type: FieldType.number },
  source_node_id: { label: "Source node", type: FieldType.number },
  target_node_id: { label: "Target node", type: FieldType.number },
  relation_type: {
    label: "Relation type",
    type: FieldType.dictionarySelect,
    dictionary: "CopTreeRelationType",
  },
  description: { label: "Description" },
  created_by: { label: "Created by", dictionary: "Users", type: FieldType.dictionarySelect },
  created_at: { label: "Created at", type: FieldType.datetime },
};

export interface TreeNodeWithChildren extends CopTreeNode {
  children: TreeNodeWithChildren[];
}

export interface LinkedNodeItem {
  link: CopTreeNodeLink;
  otherNode: CopTreeNode;
  isOutgoing: boolean;
}

const TreeNodesApiPath = "/api/cop-decision/tree/node";
const NodeLinksApiPath = "/api/cop-decision/tree/node-node-link";

export const useCopTreeNodeNodeLinks = (nodeId?: number | null) => {
  // 1. Load all tree nodes
  const treeData = useLoadedData<CopTreeNode[]>(TreeNodesApiPath, []);

  // 2. Load all links where current node is either source or target
  const linksUrl = nodeId ? `${NodeLinksApiPath}?node_id=${nodeId}` : null;
  const linksData = useLoadedData<CopTreeNodeLink[]>(linksUrl || "", [], !!linksUrl);

  // 3. Build hierarchy and resolve linked nodes
  const { nodesById, rootNodes, linkedNodes, linkedNodeIds } = useMemo(() => {
    const nodesById = new Map<number, CopTreeNode>();
    treeData.data.forEach(n => nodesById.set(n._id, n));

    const linkedNodeIds = new Set<number>();
    const linkedNodes: LinkedNodeItem[] = [];

    if (nodeId) {
      linksData.data.forEach(link => {
        const isOutgoing = link.source_node_id === nodeId;
        const otherNodeId = isOutgoing ? link.target_node_id : link.source_node_id;
        linkedNodeIds.add(otherNodeId);

        const otherNode = nodesById.get(otherNodeId);
        if (otherNode) {
          linkedNodes.push({ link, otherNode, isOutgoing });
        }
      });
    }

    // Build hierarchical tree
    const childrenByParentKey = new Map<string, TreeNodeWithChildren[]>();
    const rawRootNodes: TreeNodeWithChildren[] = [];

    treeData.data.forEach(n => {
      const treeNodeItem: TreeNodeWithChildren = { ...n, children: [] };
      if (!n.path || n.path.trim() === "") {
        rawRootNodes.push(treeNodeItem);
      } else {
        const list = childrenByParentKey.get(n.path) || [];
        list.push(treeNodeItem);
        childrenByParentKey.set(n.path, list);
      }
    });

    const attachChildren = (parent: TreeNodeWithChildren) => {
      const parentKey = parent.path ? `${parent.path}.${parent._id}` : `${parent._id}`;
      const children = childrenByParentKey.get(parentKey) || [];
      children.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      parent.children = children;
      children.forEach(attachChildren);
    };

    rawRootNodes.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    rawRootNodes.forEach(attachChildren);

    return {
      nodesById,
      rootNodes: rawRootNodes,
      linkedNodes,
      linkedNodeIds,
    };
  }, [treeData.data, linksData.data, nodeId]);

  // 4. Mutations
  const addLink = useCallback(
    async (payload: { target_node_id: number; relation_type: string; description?: string | null }) => {
      if (!nodeId) return;
      await apiFetch<CopTreeNodeLink>(NodeLinksApiPath, "post", {
        source_node_id: nodeId,
        target_node_id: payload.target_node_id,
        relation_type: payload.relation_type,
        description: payload.description || null,
      });
      await linksData.reload();
    },
    [nodeId, linksData]
  );

  const removeLink = useCallback(
    async (linkId: number) => {
      await apiFetch<CopTreeNodeLink>(`${NodeLinksApiPath}/${linkId}`, "delete");
      await linksData.reload();
    },
    [linksData]
  );

  return {
    isLoading: treeData.isLoading || linksData.isLoading,
    allNodes: treeData.data,
    nodesById,
    rootNodes,
    linkedNodes,
    linkedNodeIds,
    addLink,
    removeLink,
    reloadLinks: linksData.reload,
  };
};